import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import { DOCUMENT_ACCEPT } from '../../../utils/documentAccept';
import { employeeDisplayName } from '../../ats-requisitions/employeeOption';
import { useAuth } from '../../../context/AuthContext';
import { multiSelectFilter, tagFilterColumn } from './tagColumns';
import { fetchTagCatalog } from './tagApi';
import { fetchPoolOptions } from '../../ats-candidate-pools/config/poolApi';

// Elle aday girişinde kullanılabilen para birimleri — kadro talebi formuyla
// birebir aynı liste (ats-requisitions/config).
export const CANDIDATE_CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];

export const CANDIDATE_SOURCES = [
  'linkedin',
  'indeed',
  'kariyernet',
  'yenibiris',
  'career_site',
  'referral',
  'external_agency',
  'talent_pool',
  'boomerang',
];

const useAtsCandidatesApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);
  // Sağlayıcısız (izole hook testi) çağrıda bağlam boş — okuma yetkisine düşülür.
  const { canWrite } = useAuth() || {};
  const writable = canWrite ? canWrite('AT') : false;

  // Etiket kataloğu (liste süzgeci + rozetler) ve — YALNIZ AT.Write'a — havuz listesi
  // (`poolId[eq]` süzgeci). Havuzlar tüm adayları içerir; AT.Read'e hiç sorulmaz.
  const [tagCatalog, setTagCatalog] = useState([]);
  const [poolOptions, setPoolOptions] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchTagCatalog().then(({ items }) => { if (alive) setTagCatalog(items); });
    if (writable) fetchPoolOptions().then(({ items }) => { if (alive) setPoolOptions(items); });
    return () => { alive = false; };
  }, [writable]);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/ats-candidates?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const remove = useCallback(async (id) => {
    try {
      await api.delete(`/v1/ats-candidates/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => {
    const sourceLabel = (s) => t(`ats.candidate.source.${s}`, { defaultValue: s });

    // Sıralama: backend AtsCandidateDataAccess orderBy switch'i fullname | source | fraudscore
    // (varsayılan dal CreateDate) — `sortKey` bunlara eşler; diğer kolonlar `sort: false`.
    //
    // FE-35 (2026-09-12): "Sahtecilik Skoru" kolonu KALDIRILDI. İki gerekçe:
    // (1) alan hiçbir yerde HESAPLANMIYOR — AtsCandidateDataAccess yalnız
    //     `FraudScore = 0m` yazıyor, yani kolon her satırda "0,00" gösteriyordu;
    // (2) her AT.Read kullanıcısına adayın yanında "sahtecilik" etiketi çıkarmak
    //     jargon ve yanıltıcı. Arka uçtaki alan/filtre/sıralama DURUYOR (ileride
    //     gerçek bir skor hesaplanırsa kolon geri gelir) — silinen yalnız ekran.
    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'fullName',
        label: t('ats.candidate.fullName'),
        options: {
          filter: true,
          sort: true,
          sortKey: 'fullname',
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
        },
      },
      {
        name: 'email',
        label: t('ats.candidate.email'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
        },
      },
      {
        name: 'phone',
        label: t('ats.candidate.phone'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
          customBodyRender: (v) => v || '-',
        },
      },
      {
        name: 'source',
        label: t('ats.candidate.sourceLabel'),
        options: {
          ...multiSelectFilter(t('ats.candidate.sourceLabel'), CANDIDATE_SOURCES, sourceLabel),
          sortKey: 'source',
          customBodyRender: (v) => sourceLabel(v),
        },
      },
      {
        name: 'currentTitle',
        label: t('ats.candidate.currentTitle'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => v || '-',
        },
      },
      numericFilterColumn('experienceYears', t('ats.candidate.experienceYears'), { decimals: 0, sort: false }),
      {
        name: 'location',
        label: t('ats.candidate.location'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
          customBodyRender: (v) => v || '-',
        },
      },
      numericFilterColumn('expectedSalary', t('ats.candidate.expectedSalary'), { decimals: 2, sort: false }),
      {
        name: 'isAlumni',
        label: t('ats.candidate.isAlumni'),
        options: {
          filter: true,
          sort: false,
          filterType: 'custom',
          filterOptions: {
            defaultOp: 'eq',
            display: (filterList, onChange, index, column) => {
              const current = filterList[index] && filterList[index][0] !== undefined
                ? filterList[index][0]
                : '';
              return (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>{t('ats.candidate.isAlumni')}</InputLabel>
                  <Select
                    value={current}
                    onChange={(e) => {
                      const v = e.target.value;
                      filterList[index] = v === '' ? [] : [v];
                      onChange(filterList[index], index, column);
                    }}
                  >
                    <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
                    <MenuItem value="true">{t('common.yes')}</MenuItem>
                    <MenuItem value="false">{t('common.no')}</MenuItem>
                  </Select>
                </FormControl>
              );
            },
          },
          customBodyRender: (v) => v ? '✓' : '-',
        },
      },
      tagFilterColumn(t('ats.candidateTag.tags'), tagCatalog),
      // Havuz süzgeci — kolon GÖRÜNMEZ (satırda havuz yok), yalnız süzgeç panelinde
      // çıkar ve `poolId[eq]` gönderir. AT.Read'de süzgeç de yok.
      {
        name: 'poolId',
        label: t('ats.candidatePool.pool'),
        options: {
          display: false,
          viewColumns: false,
          download: false,
          print: false,
          sort: false,
          filter: writable && poolOptions.length > 0,
          filterType: 'custom',
          filterOptions: {
            defaultOp: 'eq',
            display: (filterList, onChange, index, column) => {
              const current = filterList[index] && filterList[index][0] ? filterList[index][0] : '';
              return (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>{t('ats.candidatePool.pool')}</InputLabel>
                  <Select
                    value={current}
                    onChange={(e) => {
                      const v = e.target.value;
                      filterList[index] = v ? [v] : [];
                      onChange(filterList[index], index, column);
                    }}
                  >
                    <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
                    {poolOptions.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            },
          },
          customBodyRender: () => null,
        },
      },
      {
        name: 'applicationCount',
        label: t('ats.candidate.applicationCount'),
        options: { filter: false, sort: false },
      },
      {
        name: 'lastJobPostingTitle',
        label: t('ats.candidate.lastJobPosting'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => v || '-',
        },
      },
      dateFilterColumn('consentExpiresAt', t('ats.candidate.consentExpiresAt'), lang, { sort: false }),
      // createdate switch'te ayrı bir dal değil: bilinmeyen anahtar CreateDate'e (varsayılan) düşer,
      // yön korunur — sıralama doğru çalışır.
      dateFilterColumn('createDate', t('ats.candidate.createDate'), lang, { sortKey: 'createdate' }),
    ];
  }, [t, lang, tagCatalog, poolOptions, writable]);

  // ------------------------------------------------------------ FE-11: yazma
  //
  // Kariyer sitesi dışındaki her kanal (ajans, referans, fuar, yanlış yazılmış
  // e-posta) bugüne kadar sisteme GİREMİYORDU: arka uçta POST/PUT hazırdı ama
  // ekranda ne "Yeni Aday" ne "Düzenle" vardı.
  //
  // CV: `pictureUpload` presign → R2'ye PUT → adres akışını GenericFormDrawer'ın
  // kendi içinde yürütür (bkz. uploadSchemaFiles), yani `onSave`e ulaşan
  // formData.resumeFileUrl ZATEN bu kullanıcıya ihraç edilmiş gerçek adrestir.
  // Elle adres yazdırmak ÇALIŞMAZ: AtsCandidateDataAccess'teki yazma kapısı
  // (AtsCandidateWriteGuardTests, 9 vaka) ihraç kaydı olmayan her adresi reddeder.
  // `purpose: 'candidateResume'` ÖZEL kovayı seçer (StoragePurposes).
  const buildPayload = (formData) => ({
    fullName: formData.fullName,
    email: formData.email,
    phone: formData.phone || null,
    linkedInUrl: formData.linkedInUrl || null,
    resumeFileUrl: formData.resumeFileUrl || null,
    coverLetter: formData.coverLetter || null,
    currentEmployer: formData.currentEmployer || null,
    currentTitle: formData.currentTitle || null,
    experienceYears: formData.experienceYears === '' || formData.experienceYears == null
      ? null : Number(formData.experienceYears),
    location: formData.location || null,
    expectedSalary: formData.expectedSalary === '' || formData.expectedSalary == null
      ? null : Number(formData.expectedSalary),
    expectedSalaryCurrency: formData.expectedSalaryCurrency || null,
    noticePeriodDays: formData.noticePeriodDays === '' || formData.noticePeriodDays == null
      ? null : Number(formData.noticePeriodDays),
    source: formData.source,
    sourceDetail: formData.sourceDetail || null,
  });

  // MÜKERRER ADAY: backend aynı normalize e-postayla ikinci ELLE kaydı 409
  // `ats.candidate.duplicateEmail` ile reddeder. Hata mesajını interceptor
  // gösterir (ev kuralı: toast yalnız interceptor'da); burada yalnız SONUCUN
  // TÜRÜ çağırana taşınır ki sayfa listeyi o e-postaya süzüp var olan kaydı
  // gösterebilsin — kullanıcı "neden olmadı" ile baş başa kalmasın.
  const create = useCallback(async (formData) => {
    try {
      await api.post('/v1/ats-candidates', buildPayload(formData));
      refresh();
      return { ok: true };
    } catch (err) {
      if (err?.response?.data?.message === 'ats.candidate.duplicateEmail') {
        return { ok: false, duplicateEmail: (formData.email || '').trim() };
      }
      return { ok: false };
    }
  }, [refresh]);

  const update = useCallback(async (id, formData) => {
    try {
      await api.put(`/v1/ats-candidates/${id}`, buildPayload(formData));
      refresh();
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }, [refresh]);

  // Adayın adına başvuru açma. Mükerrer AKTİF başvuru kapısı arka uçta zaten
  // var (`ats.application.duplicateActive`, normalize e-posta üzerinden).
  const createApplication = useCallback(async (candidateId, formData) => {
    try {
      await api.post('/v1/ats-applications', {
        candidateId,
        jobPostingId: formData.jobPostingId,
        assignedRecruiterUserId: formData.assignedRecruiterUserId || null,
      });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const sourceOptions = useMemo(
    () => CANDIDATE_SOURCES.map((c) => ({ id: c, name: t(`ats.candidate.source.${c}`, { defaultValue: c }) })),
    [t],
  );
  const currencyOptions = useMemo(
    () => CANDIDATE_CURRENCIES.map((c) => ({ id: c, name: c })),
    [],
  );

  const objectSchema = useMemo(() => [
    { name: 'fullName', label: t('ats.candidate.fullName'), type: 'text', grid: 12, required: true },
    { name: 'email', label: t('ats.candidate.email'), type: 'email', grid: 6, required: true },
    { name: 'phone', label: t('ats.candidate.phone'), type: 'phone', grid: 6 },
    {
      name: 'source', label: t('ats.candidate.sourceLabel'), type: 'select', grid: 6, required: true,
      options: sourceOptions, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'sourceDetail', label: t('ats.candidate.sourceDetail'), type: 'text', grid: 6,
      description: t('ats.candidate.sourceDetailHint'),
    },
    {
      name: 'resumeFileUrl', label: t('ats.candidate.resume'), type: 'pictureUpload', grid: 12,
      purpose: 'candidateResume', accept: DOCUMENT_ACCEPT, maxSize: 10,
    },
    { name: 'linkedInUrl', label: t('ats.candidate.linkedInUrl'), type: 'text', grid: 12 },
    { name: 'currentEmployer', label: t('ats.candidate.currentEmployer'), type: 'text', grid: 6 },
    { name: 'currentTitle', label: t('ats.candidate.currentTitle'), type: 'text', grid: 6 },
    { name: 'experienceYears', label: t('ats.candidate.experienceYears'), type: 'number', grid: 6 },
    { name: 'location', label: t('ats.candidate.location'), type: 'text', grid: 6 },
    { name: 'expectedSalary', label: t('ats.candidate.expectedSalary'), type: 'number', grid: 6 },
    {
      name: 'expectedSalaryCurrency', label: t('ats.candidate.expectedSalaryCurrency'), type: 'select', grid: 6,
      options: currencyOptions, idKey: 'id', labelKey: 'name',
    },
    { name: 'noticePeriodDays', label: t('ats.candidate.noticePeriodDays'), type: 'number', grid: 6 },
    { name: 'coverLetter', label: t('ats.candidate.coverLetter'), type: 'textarea', grid: 12, rows: 5 },
  ], [t, sourceOptions, currencyOptions]);

  // TUZAK: `asyncSelect` YALNIZ `onSearch`u okur — `asyncConfig` ölü alandır.
  const applicationSchema = useMemo(() => [
    {
      name: 'jobPostingId', label: t('ats.candidate.jobPostingId'), type: 'asyncSelect', grid: 12, required: true,
      description: t('ats.candidate.jobPostingIdHint'),
      onSearch: async (q) => {
        // BP-16 — arka uçta `published` artık YALNIZ süresi dolmamış satırları
        // getirir (süresi geçmiş yayındaki ilan `expired` olarak türetilir).
        // İşe alım uzmanı geç gelen bir kâğıt başvuruyu süresi geçmiş ilana da
        // işleyebilmeli, bu yüzden seçici İKİSİNİ de ister; kariyer sitesi ise
        // (aday tarafı) süresi geçmiş ilanı göstermeye devam etmez.
        const params = { 'status[in]': 'published,expired', pageSize: 30 };
        if (q) params['title[like]'] = q;
        const res = await api.get('/v1/ats-job-postings', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((it) => ({ id: it.id, name: it.title }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/ats-job-postings/${id}`);
        const it = res.data?.data;
        return { id, name: it?.title || '' };
      },
    },
    {
      name: 'assignedRecruiterUserId', label: t('ats.candidate.assignedRecruiterUserId'),
      type: 'asyncSelect', grid: 12,
      onSearch: async (q) => {
        const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((e) => ({ id: e.id, name: employeeDisplayName(e), profilePicture: e.profilePicture }));
      },
      onGetById: async (id) => {
        try {
          const res = await api.get(`/v1/employees/${id}`);
          const e = res.data?.data || {};
          return { id, name: employeeDisplayName(e) || id, profilePicture: e.profilePicture };
        } catch { return { id, name: '', profilePicture: null }; }
      },
    },
  ], [t]);

  return {
    fetching, data, pagination, columns, refresh, remove, tagCatalog, poolOptions,
    objectSchema, applicationSchema, create, update, createApplication,
  };
};

export default useAtsCandidatesApi;
